import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Circle } from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

const TypingIndicator = () => (
  <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
    <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center p-1">
      <img
        src="/neo_ami.png"
        alt="AMI"
        className="w-6 h-6 rounded-full object-contain bg-black"
      />
    </div>
    <div className="flex items-center gap-1">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </div>
      <span className="text-base text-gray-500 ml-2">AI is typing...</span>
    </div>
  </div>
);

const ChatWindow = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm your AMI AI. How can I help you today?",
      fromUser: false,
      timestamp: new Date(),
      id: 1,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showWindow, setShowWindow] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Configure marked for safe markdown rendering
  useEffect(() => {
    marked.setOptions({
      gfm: true,
      breaks: true,
      sanitize: false, // We'll use DOMPurify for sanitization
    });
  }, []);

  // Animate window entrance
  useEffect(() => {
    setShowWindow(true);
    inputRef.current?.focus();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format message text (handles markdown, newlines, and sanitization)
  const formatMessage = (text) => {
    // Convert markdown to HTML
    let formattedText = marked.parse(text || '');
    // Sanitize the HTML to prevent XSS
    formattedText = DOMPurify.sanitize(formattedText, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
    return formattedText;
  };

  const handleSend = async () => {
    if (input.trim() === '') return;
  
    const userMessage = {
      text: input,
      fromUser: true,
      timestamp: new Date(),
      id: Date.now(),
    };
  
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
  
    try {
      const response = await fetch('https://disha-pocket.neophyte.live/sentinel_agent/ask/vinay123', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: input }), // Removed extra parenthesis
      });
  
      const data = await response.json();
  
      const aiMessage = {
        text: data.response || 'Sorry, no response.',
        fromUser: false,
        timestamp: new Date(),
        id: Date.now() + 1,
      };
  
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error calling backend:', error);
      setMessages((prev) => [
        ...prev,
        {
          text: 'Failed to get a response from the server.',
          fromUser: false,
          timestamp: new Date(),
          id: Date.now() + 1,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setShowWindow(false);
    setTimeout(() => onClose(), 300);
  };

  return (
    <div
      className={`fixed top-0 right-0 h-screen z-[1300] transition-all duration-300 ease-out ${
        showWindow ? 'translate-x-0' : 'translate-x-full'
      } w-full sm:w-3/4 md:w-1/2 lg:w-2/5 xl:w-1/3`}
    >
      <div className="bg-white h-full shadow-2xl border-l border-gray-200 flex flex-col rounded-l-3xl rounded-bl-3xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center overflow-hidden border-2 border-yellow-400 p-1">
              <img src="/neo_ami.png" alt="AMI" className="w-6 h-6 object-contain rounded-full" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">AMI AI</h3>
              <div className="flex items-center gap-2 text-sm opacity-90">
                <Circle className="w-2 h-2 fill-green-400 text-green-400" />
                Online
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-full transition-all duration-200 hover:scale-110"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 custom-scrollbar">
          {messages.map((msg, index) => (
            <div
              key={msg.id}
              className={`flex ${msg.fromUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div
                className={`flex items-end gap-2 max-w-xs lg:max-w-md ${
                  msg.fromUser ? 'flex-row-reverse' : 'flex-row'
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  {msg.fromUser ? (
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-black rounded-full p-1 flex items-center justify-center">
                      <img
                        src="/neo_ami.png"
                        alt="AMI"
                        className="w-full h-full rounded-full object-contain"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <div
                    className={`px-4 py-2 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                      msg.fromUser
                        ? 'bg-blue-500 text-white rounded-br-md'
                        : 'bg-white text-gray-800 rounded-bl-md border border-gray-200'
                    }`}
                  >
                    <div
                      className="text-base leading-relaxed break-words"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.text) }}
                    />
                  </div>
                  <p
                    className={`text-xs text-gray-500 mt-1 ${msg.fromUser ? 'text-right' : 'text-left'}`}
                  >
                    {formatTime(msg.timestamp)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="animate-fade-in">
              <TypingIndicator />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-200">
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder="Type your message..."
                rows={1}
                className={`w-full px-4 py-3 rounded-2xl border-2 resize-none transition-all duration-200 focus:outline-none ${
                  isInputFocused
                    ? 'border-blue-500 shadow-lg shadow-blue-500/20'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                style={{
                  minHeight: '48px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                input.trim() && !isTyping
                  ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl hover:scale-110 active:scale-95'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Style for rendered markdown */
        .text-base a {
          color: #3b82f6;
          text-decoration: underline;
        }
        .text-base a:hover {
          color: #2563eb;
        }
        .text-base strong {
          font-weight: 600;
        }
        .text-base em {
          font-style: italic;
        }
        .text-base code {
          background: #f3f4f6;
          padding: 2px 4px;
          border-radius: 3px;
        }
        .text-base pre {
          background: #f3f4f6;
          padding: 8px;
          border-radius: 5px;
          overflow-x: auto;
        }
        .text-base ul,
        .text-base ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        .text-base li {
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  );
};

export default ChatWindow;